"use client";

import { c as _c } from "react/compiler-runtime";
import { LocationNoticeLine } from "../../components/location-notice";
import { useQuery } from "@tanstack/react-query";
import { addMonths, endOfMonth, endOfWeek, format, isSameDay, startOfMonth, startOfWeek } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowRight } from "react-icons/fa";
import { useParams } from "next/navigation";
import { Button, Calendar } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { toClinicDateKey } from "@/lib/format-data";
import { noProvidersUntil, selectionShiftStart } from "@/lib/self-schedule-next-shift";
import { MIXED_DELIVERY_MESSAGE, cartMixesDelivery } from "@/lib/self-schedule-virtual-services";
import { flattenServiceIds } from "@/lib/service-addon-utils";
import { cx } from "@/lib/tailwind-utils";
import { useStoreTimezone } from "@/lib/timezone";
import { GroupedAvailability } from "@/types/self-schedule";
import JoinWaitlist from "./join-waitlist";
import TimeGroup from "./time-group";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { tenantQueryOptions } from "@/query/tenant";
import { useSelfScheduleStore } from "@/stores/self-schedule-store";
export enum TimeSlotGroup {
  morning = "morning",
  afternoon = "afternoon",
  evening = "evening",
}
type AvailabilitySlot = GroupedAvailability["morning"][number];
type TimeSlot = Pick<AvailabilitySlot, "start" | "end" | "assignments"> & {
  locationId?: string | null;
};

/** "Central Time", for a list whose times come from clinics in other zones. */
const zoneName = (timeZone: string, at: Date) => new Intl.DateTimeFormat("en-US", {
  timeZone,
  timeZoneName: "longGeneric"
}).formatToParts(at).find(part => part.type === "timeZoneName")?.value ?? timeZone;
const hasAvailability = (group: GroupedAvailability) => group.morning.length > 0 || group.afternoon.length > 0 || group.evening.length > 0;
const dedupeSlotsByStartTime = (slots: AvailabilitySlot[]) => {
  const seenStartTimes = new Set<number>();
  return slots.filter(slot => {
    const startTime = slot.start.getTime();
    if (seenStartTimes.has(startTime)) return false;
    seenStartTimes.add(startTime);
    return true;
  });
};
const slotsForDate = (slots: AvailabilitySlot[], dateKey: string) => dedupeSlotsByStartTime(slots.filter(slot => toClinicDateKey(slot.start) === dateKey));
const availabilityForDate = (availability: GroupedAvailability[], selectedDate: Date) => {
  const dateKey = toClinicDateKey(selectedDate);
  const group = availability.find(item => toClinicDateKey(new Date(item.date)) === dateKey);
  if (!group) return null;
  return {
    ...group,
    morning: slotsForDate(group.morning, dateKey),
    afternoon: slotsForDate(group.afternoon, dateKey),
    evening: slotsForDate(group.evening, dateKey)
  };
};
interface TimePickerProps {
  className?: string;
}
const TimePicker = t0 => {
  const $ = _c(152);
  const {
    className
  } = t0;
  const {
    selectedServices,
    selectedLocation,
    selectedDateTime,
    clearSelectedDateTime,
    selectAvailabilitySlot,
    requestedAssignments,
    selectedAddons,
    bookingEvent,
    waitlistId,
    searchesEveryLocation
  } = useSelfScheduleContext();
  const cancelEventId = useSelfScheduleStore(_temp);
  const timezone = useStoreTimezone();
  let t1;
  if ($[0] !== selectedAddons || $[1] !== selectedServices) {
    t1 = selectedServices ? flattenServiceIds(selectedServices, selectedAddons) : [];
    $[0] = selectedAddons;
    $[1] = selectedServices;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const allServiceIds = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = selfScheduleQueryOptions.frontPage();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== selectedLocation) {
    t3 = {
      ...t2,
      select: data => data && {
        services: data.services,
        providers: data.providers,
        nextShiftStarts: selectedLocation && data.nextShiftStarts ? data.nextShiftStarts[selectedLocation.id] ?? {} : undefined
      }
    };
    $[4] = selectedLocation;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: frontPage,
    isLoading: isFrontPageLoading
  } = useQuery(t3);
  const appointments = frontPage?.services;
  let providersBackOn;
  let t4;
  if ($[6] !== appointments || $[7] !== bookingEvent || $[8] !== frontPage || $[9] !== requestedAssignments || $[10] !== selectedDateTime || $[11] !== selectedLocation) {
    const selectionShift = selectedLocation && frontPage?.nextShiftStarts ? selectionShiftStart({
      nextShiftStarts: frontPage.nextShiftStarts,
      assignments: requestedAssignments,
      services: appointments ?? [],
      providers: frontPage.providers,
      locationId: selectedLocation.id
    }) : null;
    providersBackOn = selectedLocation && frontPage?.nextShiftStarts && !bookingEvent ? noProvidersUntil(selectionShift, selectedLocation.timezone) : null;
    t4 = selectedDateTime ?? (bookingEvent ? new Date(bookingEvent.start) : providersBackOn !== "indefinitely" && providersBackOn && selectionShift ? new Date(selectionShift) : new Date());
    $[6] = appointments;
    $[7] = bookingEvent;
    $[8] = frontPage;
    $[9] = requestedAssignments;
    $[10] = selectedDateTime;
    $[11] = selectedLocation;
    $[12] = providersBackOn;
    $[13] = t4;
  } else {
    providersBackOn = $[12];
    t4 = $[13];
  }
  const initialDate = t4;
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [currentMonth, setCurrentMonth] = useState(initialDate);
  const timeSlotsRef = useRef(null);
  let now;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    now = new Date();
    now.setSeconds(0, 0);
    $[14] = now;
  } else {
    now = $[14];
  }
  let t5;
  if ($[15] !== currentMonth) {
    t5 = startOfWeek(startOfMonth(currentMonth), {
      weekStartsOn: 0
    });
    $[15] = currentMonth;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const firstVisibleDay = t5;
  const startDate = now > firstVisibleDay ? now : firstVisibleDay;
  let t6;
  if ($[17] !== currentMonth) {
    t6 = endOfWeek(endOfMonth(currentMonth), {
      weekStartsOn: 0
    });
    $[17] = currentMonth;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const endDate = t6;
  let t7;
  if ($[19] !== endDate || $[20] !== startDate) {
    t7 = [startDate, endDate];
    $[19] = endDate;
    $[20] = startDate;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const dateRange = t7;
  const hasBookableRange = dateRange[1] > dateRange[0];
  let t8;
  bb0: {
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = startOfMonth(new Date());
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    const thisMonth = t9;
    if (!bookingEvent) {
      t8 = thisMonth;
      break bb0;
    }
    const eventMonth = startOfMonth(bookingEvent.start);
    t8 = eventMonth > thisMonth ? eventMonth : thisMonth;
  }
  const earliestBookableMonth = t8;
  let t9;
  if ($[23] !== bookingEvent) {
    t9 = bookingEvent ? startOfMonth(bookingEvent.end) : undefined;
    $[23] = bookingEvent;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const latestBookableMonth = t9;
  const params = useParams();
  let t10;
  if ($[25] !== params?.tenant) {
    t10 = tenantQueryOptions.public(params?.tenant);
    $[25] = params?.tenant;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const {
    data: waitlistTenant
  } = useQuery(t10);
  let t11;
  if ($[27] !== waitlistTenant?.enabledModules) {
    t11 = waitlistTenant?.enabledModules?.includes("WAITLIST") ?? false;
    $[27] = waitlistTenant?.enabledModules;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const hasWaitlistModule = t11;
  let t12;
  if ($[29] !== selectedLocation || $[30] !== selectedServices) {
    t12 = cartMixesDelivery(selectedServices, selectedLocation);
    $[29] = selectedLocation;
    $[30] = selectedServices;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const mixesDelivery = t12;
  let t13;
  if ($[32] !== bookingEvent?.id || $[33] !== cancelEventId || $[34] !== dateRange[0] || $[35] !== dateRange[1] || $[36] !== requestedAssignments || $[37] !== searchesEveryLocation || $[38] !== selectedAddons || $[39] !== selectedLocation?.id || $[40] !== timezone) {
    t13 = selfScheduleQueryOptions.availability(requestedAssignments, dateRange[0], dateRange[1], searchesEveryLocation ? {
      timezone
    } : {
      locationId: selectedLocation?.id ?? ""
    }, selectedAddons, bookingEvent?.id ?? null, cancelEventId);
    $[32] = bookingEvent?.id;
    $[33] = cancelEventId;
    $[34] = dateRange[0];
    $[35] = dateRange[1];
    $[36] = requestedAssignments;
    $[37] = searchesEveryLocation;
    $[38] = selectedAddons;
    $[39] = selectedLocation?.id;
    $[40] = timezone;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const t14 = allServiceIds.length > 0 && (searchesEveryLocation || !!selectedLocation?.id) && hasBookableRange && !mixesDelivery;
  let t15;
  if ($[42] !== t13 || $[43] !== t14) {
    t15 = {
      ...t13,
      enabled: t14
    };
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const {
    data: availability,
    isLoading: isAvailabilityLoading
  } = useQuery(t15);
  let t16;
  if ($[45] !== availability) {
    t16 = availability?.filter(hasAvailability).map(_temp2) ?? [];
    $[45] = availability;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const availableDates = t16;
  let t17;
  if ($[47] !== availability || $[48] !== bookingEvent || $[49] !== waitlistId) {
    t17 = availability?.filter(group_0 => hasAvailability(group_0) || !bookingEvent && !waitlistId && group_0.hasShift).map(_temp3) ?? [];
    $[47] = availability;
    $[48] = bookingEvent;
    $[49] = waitlistId;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  const selectableDates = t17;
  let t18;
  if ($[51] !== availability || $[52] !== selectedDate) {
    t18 = availability ? availabilityForDate(availability, selectedDate) : null;
    $[51] = availability;
    $[52] = selectedDate;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  const timeSlots = t18;
  let t19;
  if ($[54] !== timeSlots) {
    t19 = !!timeSlots && hasAvailability(timeSlots);
    $[54] = timeSlots;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  const hasTimeSlots = t19;
  const isWaitlistOnlyDate = !bookingEvent && !!timeSlots?.hasShift && !hasTimeSlots;
  const showsNoTimes = !isAvailabilityLoading && !hasTimeSlots && !isWaitlistOnlyDate && !mixesDelivery;
  let t20;
  if ($[56] !== availableDates || $[57] !== currentMonth || $[58] !== selectedDate) {
    t20 = () => {
      if (!availableDates.length) {
        setCurrentMonth(addMonths(currentMonth, 1));
        return;
      }
      setSelectedDate(availableDates.find(date => date > selectedDate) ?? availableDates[0]);
    };
    $[56] = availableDates;
    $[57] = currentMonth;
    $[58] = selectedDate;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  const goToNextAvailableDate = t20;
  let t21;
  if ($[60] !== clearSelectedDateTime || $[61] !== selectedDateTime) {
    t21 = date_0 => {
      if (!date_0) {
        return;
      }
      setSelectedDate(date_0);
      if (selectedDateTime && toClinicDateKey(selectedDateTime) !== toClinicDateKey(date_0)) {
        clearSelectedDateTime();
      }
    };
    $[60] = clearSelectedDateTime;
    $[61] = selectedDateTime;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  const handleDateSelect = t21;
  let t22;
  if ($[63] !== selectAvailabilitySlot || $[64] !== selectedDate) {
    t22 = slot => {
      if (toClinicDateKey(slot.start) !== toClinicDateKey(selectedDate)) {
        return;
      }
      selectAvailabilitySlot(slot.start, slot.assignments, slot.locationId ?? null, slot.end);
    };
    $[63] = selectAvailabilitySlot;
    $[64] = selectedDate;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  const handleTimeSelect = t22;
  let t23;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      timeSlotsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    };
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== selectedDate) {
    t24 = [selectedDate];
    $[67] = selectedDate;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  useEffect(t23, t24);
  if (!appointments || isFrontPageLoading) {
    return null;
  }
  let t25;
  if ($[69] !== className) {
    t25 = cx("motion-ease-spring-bouncy", "motion-scale-in-105 motion-opacity-in-85", className);
    $[69] = className;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  let t27;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <h1 className="text-h1 text-foreground">Choose a Time</h1>;
    t27 = <p className="text-body text-muted-foreground">When would you like to visit?</p>;
    $[71] = t26;
    $[72] = t27;
  } else {
    t26 = $[71];
    t27 = $[72];
  }
  let t28;
  if ($[73] !== searchesEveryLocation || $[74] !== selectedDate || $[75] !== timezone) {
    t28 = searchesEveryLocation && <p className="text-caption text-muted-foreground">Times are shown in {zoneName(timezone, selectedDate)}.</p>;
    $[73] = searchesEveryLocation;
    $[74] = selectedDate;
    $[75] = timezone;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== providersBackOn || $[78] !== selectedLocation?.isVirtual) {
    t29 = providersBackOn && <LocationNoticeLine until={providersBackOn} isVirtual={selectedLocation?.isVirtual} className="mt-2" />;
    $[77] = providersBackOn;
    $[78] = selectedLocation?.isVirtual;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== t28 || $[81] !== t29) {
    t30 = <div className="mb-6">{t26}{t27}{t28}{t29}</div>;
    $[80] = t28;
    $[81] = t29;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = cx("space-y-4 md:w-fit");
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  let t32;
  if ($[84] !== selectableDates) {
    t32 = date_1 => !selectableDates.some(availableDate => isSameDay(date_1, availableDate));
    $[84] = selectableDates;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== selectableDates || $[87] !== selectedDate) {
    let t34;
    if ($[89] !== selectedDate) {
      t34 = date_2 => !isSameDay(date_2, selectedDate);
      $[89] = selectedDate;
      $[90] = t34;
    } else {
      t34 = $[90];
    }
    t33 = selectableDates.filter(t34);
    $[86] = selectableDates;
    $[87] = selectedDate;
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  let t34;
  if ($[91] !== availableDates || $[92] !== selectedDate) {
    let t35;
    if ($[94] !== selectedDate) {
      t35 = date_3 => !isSameDay(date_3, selectedDate);
      $[94] = selectedDate;
      $[95] = t35;
    } else {
      t35 = $[95];
    }
    t34 = availableDates.filter(t35);
    $[91] = availableDates;
    $[92] = selectedDate;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[96] !== t33 || $[97] !== t34) {
    t35 = {
      shift: t33,
      available: t34
    };
    $[96] = t33;
    $[97] = t34;
    $[98] = t35;
  } else {
    t35 = $[98];
  }
  let t36;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      shift: "!text-foreground",
      available: "!text-foreground after:w-[3px] after:bg-primary after:rounded-full after:absolute after:bottom-1.5 after:left-1/2 after:-translate-x-1/2 after:aspect-square",
      selected: "bg-primary text-primary-foreground hover:bg-primary/90"
    };
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  let t37;
  if ($[100] !== currentMonth || $[101] !== earliestBookableMonth || $[102] !== handleDateSelect || $[103] !== isAvailabilityLoading || $[104] !== latestBookableMonth || $[105] !== selectedDate || $[106] !== t32 || $[107] !== t35) {
    t37 = <div className={t31}><Calendar mode="single" selected={selectedDate} loading={isAvailabilityLoading} onSelect={handleDateSelect} month={currentMonth} onMonthChange={setCurrentMonth} fromMonth={earliestBookableMonth} toMonth={latestBookableMonth} weekStartsOn={0} disabled={t32} modifiers={t35} modifiersClassNames={t36} enableTodayButton={true} loadingClassName="after:bg-background/80 after:text-foreground after:content-['finding_available_times...']" className="flex max-h-full items-end justify-center overflow-hidden rounded-xl border shadow-sm md:p-2 transition-all duration-300" /></div>;
    $[100] = currentMonth;
    $[101] = earliestBookableMonth;
    $[102] = handleDateSelect;
    $[103] = isAvailabilityLoading;
    $[104] = latestBookableMonth;
    $[105] = selectedDate;
    $[106] = t32;
    $[107] = t35;
    $[108] = t37;
  } else {
    t37 = $[108];
  }
  let t38;
  if ($[109] !== selectedDate) {
    t38 = format(selectedDate, "EEEE, MMMM d");
    $[109] = selectedDate;
    $[110] = t38;
  } else {
    t38 = $[110];
  }
  let t39;
  if ($[111] !== t38) {
    t39 = <h3 className="text-h3 text-foreground">{t38}</h3>;
    $[111] = t38;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  if ($[113] !== isAvailabilityLoading) {
    t40 = isAvailabilityLoading && <div className="flex items-center justify-center py-8"><div className="text-body text-muted-foreground">Loading available times...</div></div>;
    $[113] = isAvailabilityLoading;
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  let t41;
  if ($[115] !== handleTimeSelect || $[116] !== hasTimeSlots || $[117] !== isAvailabilityLoading || $[118] !== selectedDateTime || $[119] !== timeSlots) {
    t41 = !isAvailabilityLoading && timeSlots && hasTimeSlots && <><TimeGroup title="Morning Availability" timeSlots={timeSlots.morning} selectedDateTime={selectedDateTime} onTimeSelect={handleTimeSelect} group={TimeSlotGroup.morning} /><TimeGroup title="Afternoon Availability" timeSlots={timeSlots.afternoon} selectedDateTime={selectedDateTime} onTimeSelect={handleTimeSelect} group={TimeSlotGroup.afternoon} /><TimeGroup title="Evening Availability" timeSlots={timeSlots.evening} selectedDateTime={selectedDateTime} onTimeSelect={handleTimeSelect} group={TimeSlotGroup.evening} /></>;
    $[115] = handleTimeSelect;
    $[116] = hasTimeSlots;
    $[117] = isAvailabilityLoading;
    $[118] = selectedDateTime;
    $[119] = timeSlots;
    $[120] = t41;
  } else {
    t41 = $[120];
  }
  let t42;
  if ($[121] !== mixesDelivery) {
    t42 = mixesDelivery && <p className="text-body text-muted-foreground">{MIXED_DELIVERY_MESSAGE}</p>;
    $[121] = mixesDelivery;
    $[122] = t42;
  } else {
    t42 = $[122];
  }
  let t43;
  if ($[123] !== showsNoTimes) {
    t43 = showsNoTimes && <p className="text-body text-muted-foreground">No available times for this date</p>;
    $[123] = showsNoTimes;
    $[124] = t43;
  } else {
    t43 = $[124];
  }
  let t44;
  if ($[125] !== goToNextAvailableDate || $[126] !== showsNoTimes) {
    t44 = showsNoTimes && <Button variant="secondary" onClick={goToNextAvailableDate} icon={FaArrowRight}><span>Next Available Date</span></Button>;
    $[125] = goToNextAvailableDate;
    $[126] = showsNoTimes;
    $[127] = t44;
  } else {
    t44 = $[127];
  }
  let t45;
  if ($[128] !== bookingEvent || $[129] !== hasWaitlistModule || $[130] !== selectedDate || $[131] !== selectedLocation || $[132] !== selectedServices || $[133] !== waitlistId) {
    t45 = !bookingEvent && hasWaitlistModule && !waitlistId && !selectedServices?.some(_temp4) && selectedLocation && selectedServices && selectedServices.length > 0 && <JoinWaitlist selectedDate={selectedDate} />;
    $[128] = bookingEvent;
    $[129] = hasWaitlistModule;
    $[130] = selectedDate;
    $[131] = selectedLocation;
    $[132] = selectedServices;
    $[133] = waitlistId;
    $[134] = t45;
  } else {
    t45 = $[134];
  }
  let t46;
  if ($[135] !== t44 || $[136] !== t45) {
    t46 = <div className="flex flex-wrap items-center gap-2">{t44}{t45}</div>;
    $[135] = t44;
    $[136] = t45;
    $[137] = t46;
  } else {
    t46 = $[137];
  }
  let t47;
  if ($[138] !== t39 || $[139] !== t40 || $[140] !== t41 || $[141] !== t42 || $[142] !== t43 || $[143] !== t46) {
    t47 = <div ref={timeSlotsRef} className="space-y-4">{t39}{t40}{t41}{t42}{t43}{t46}</div>;
    $[138] = t39;
    $[139] = t40;
    $[140] = t41;
    $[141] = t42;
    $[142] = t43;
    $[143] = t46;
    $[144] = t47;
  } else {
    t47 = $[144];
  }
  let t48;
  if ($[145] !== t37 || $[146] !== t47) {
    t48 = <div className="flex md:flex-row flex-col md:gap-12 gap-6">{t37}{t47}</div>;
    $[145] = t37;
    $[146] = t47;
    $[147] = t48;
  } else {
    t48 = $[147];
  }
  let t49;
  if ($[148] !== t25 || $[149] !== t30 || $[150] !== t48) {
    t49 = <div data-testid="step-time" className={t25} key="time-picker-container">{t30}{t48}</div>;
    $[148] = t25;
    $[149] = t30;
    $[150] = t48;
    $[151] = t49;
  } else {
    t49 = $[151];
  }
  return t49;
};
export default TimePicker;
function _temp(state) {
  return state.cancelEventId;
}
function _temp2(group) {
  return new Date(group.date);
}
function _temp3(group_1) {
  return new Date(group_1.date);
}
function _temp4(service) {
  return service.isVirtual;
}